import type { ParkingSlot, ParkingSlotListHandle } from '@/components/feature/parking/types';
import { MAP_CONTRAST_COLOR, OBJECT_GROUP } from '@/constants/floor-guide';
import { useAccessibilityTTS } from '@/hooks';
import { useFloor, useMyCurrentLocation, usePoi } from '@/hooks/map';
import { useMap } from '@/hooks/map/useMap';
import { useMapEvents } from '@/hooks/map/useMapEvents';
import { useMapMove } from '@/hooks/map/useMapMove';
import { useMapZoomLevel } from '@/hooks/map/useMapZoomLevel';
import { useCurrentBuilding } from '@/hooks/useCurrentBuilding';
import { useGlobalCountdownReset } from '@/hooks/useGlobalCountdownReset';
import { useInteraction } from '@/hooks/useInteraction';
import { useParkingNavigation, useParkingSlots } from '@/hooks/parking';
import { useConfigStore, useModalStackStore } from '@/stores';
import { generateFloorName, resetObjectStyle, updateObjectGroup, updateObjectStyle } from '@/utils/floor-guide';
import { getCurrentPois } from '@/utils/map-poi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useParkingMapView = (gnbMenuId?: string) => {
    const { t } = useTranslation();
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const didMountRef = useRef(false);
    const parkingSlotListRef = useRef<ParkingSlotListHandle>(null);

    const { speak } = useAccessibilityTTS();

    const server = useConfigStore((state) => state.config?.server);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const zoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const language = useConfigStore((state) => state.config.ui.language);

    const { resetCountdown } = useGlobalCountdownReset();
    const { currentBuilding } = useCurrentBuilding();
    const [currentMapBuildingId, setCurrentMapBuildingId] = useState<string | undefined>(() => currentBuilding?.studioBuildingId);
    const activeBuildingId = useMemo(
        () => currentMapBuildingId || currentBuilding?.studioBuildingId || '',
        [currentMapBuildingId, currentBuilding?.studioBuildingId]
    );

    const { initMap, cleanupMap, mapData, currentMap, loading, isMapReady } = useMap({
        clientId: server?.store?.mapClientId ?? '',
        clientSecret: server?.store?.mapSecret ?? '',
        studioVersion: 4,
    });

    const cleanupMapRef = useRef(cleanupMap);
    cleanupMapRef.current = cleanupMap;

    const { selectedFloorName, handleChangeFloorByName, handleChangeFloor, setSelectedFloorName } = useFloor(
        currentMap,
        mapData,
        activeBuildingId
    );

    const { handleZoomIn, handleZoomOut, isDisabledZoomIn, isDisabledZoomOut, setZoomLevel } = useMapZoomLevel(currentMap);
    const { moveUp, moveDown, moveLeft, moveRight } = useMapMove(currentMap);
    const { addMyCurrentLocation } = useMyCurrentLocation(currentMap);
    const { setPoi, resetPoi } = usePoi(currentMap);

    const {
        showPositionPopup,
        showNavigationGuide,
        navigationStatus,
        selectedPoiRef,
        selectedRoute,
        startPoint,
        navigationGuideEndPoint,
        nearbyPoiList,
        nearbySelectedPoiId,
        nearbyTitle,
        nearbyFloorName,
        floorAriaLabel,
        floorDisplayText,
        handleSelectSlot: handleNavigateToSlot,
        handleDirections,
        handleEnd,
        handlePauseSimulation,
        handleResumeSimulation,
        handleWatchAgain,
        handleNavigationComplete,
        handleFloorChanged,
    } = useParkingNavigation({
        mapData,
        currentMap,
        server,
        selectedFloorName,
        handleChangeFloorByName,
        handleChangeFloor,
        setSelectedFloorName,
        addMyCurrentLocation,
        speak,
        currentMapBuildingId,
        setCurrentMapBuildingId,
    });

    const { parkingSlots, selectedSlotId, setSelectedSlotId } = useParkingSlots({ mapData, gnbMenuId });

    const menuName = useMemo(() => {
        const buildingMenus = server?.buildingMenus;
        const menus = buildingMenus?.[language] ?? [];
        const parkingMenu = gnbMenuId ? menus.find((menu) => menu.id === gnbMenuId) : undefined;
        if (!parkingMenu?.name) return '';
        if (typeof parkingMenu.name === 'string') return parkingMenu.name;
        return parkingMenu.name.line1 ?? parkingMenu.name.line2 ?? '';
    }, [server?.buildingMenus, language, gnbMenuId]);

    const disableMouseEvent = useCallback(() => {
        currentMap?.control?.setOption({
            mouseOption: { enableZoom: false, enablePan: false },
            touchOption: { enableZoom: false, enablePan: false },
        });
    }, [currentMap]);

    const enableMouseEvent = useCallback(() => {
        currentMap?.control?.setOption({
            mouseOption: { enableZoom: true, enablePan: true },
            touchOption: { enableZoom: true, enablePan: true },
        });
    }, [currentMap]);

    const handleSelectSlot = useCallback(
        async (slot: ParkingSlot) => {
            setSelectedSlotId(slot.id);
            await handleNavigateToSlot(slot);
        },
        [handleNavigateToSlot, setSelectedSlotId]
    );

    const handleEndAndReset = useCallback(async () => {
        await handleEnd();
        setSelectedSlotId(undefined);
        parkingSlotListRef.current?.resetSlidePosition();
    }, [handleEnd, setSelectedSlotId]);

    const handleShowMyLocation = useCallback(async () => {
        const startFloorName = server?.floor?.name?.ko ?? '';
        if (startFloorName) {
            await handleChangeFloorByName(startFloorName);
        }
        await handleEnd();
        setSelectedSlotId(undefined);
        parkingSlotListRef.current?.resetSlidePosition();
        const currentPoiList = getCurrentPois(mapData, {
            floorId: server?.floor?.floor_code ?? '',
            position: {
                x: server?.machine?.positionX ?? 0,
                y: server?.machine?.positionY ?? 0,
            },
        });

        if (Array.isArray(currentPoiList) && currentPoiList.length && currentPoiList[0]?.id) {
            const allPoiItems = (server?.store?.buildings ?? []).flatMap(
                (building: { poiContents?: { studioPoiId?: string; name?: Record<string, string> }[] }) =>
                    Array.isArray(building.poiContents) ? building.poiContents : []
            );
            const poiItem = allPoiItems.find((item) => item?.studioPoiId === currentPoiList[0]?.id);
            if (poiItem) {
                speak(
                    t('floor_guide.text_to_speech.current_location', {
                        floorName: generateFloorName(startPoint?.floorName ?? startFloorName),
                        poiName: poiItem?.name?.[language] || poiItem?.name?.['ko'] || '',
                    })
                );
            }
        }
    }, [
        handleChangeFloorByName,
        speak,
        t,
        server?.floor?.name,
        server?.floor?.floor_code,
        server?.machine?.positionX,
        server?.machine?.positionY,
        server?.store?.buildings,
        mapData,
        startPoint?.floorName,
        language,
        handleEnd,
        setSelectedSlotId,
    ]);

    useMapEvents({
        mapContainer: mapContainerRef,
        onFloorChanged: (data) => {
            handleFloorChanged(data);
            if (contrast) {
                updateObjectGroup(mapData, OBJECT_GROUP);
                updateObjectStyle(currentMap, OBJECT_GROUP);
                setPoi(MAP_CONTRAST_COLOR.POI);
            } else {
                resetObjectStyle(currentMap);
                resetPoi();
            }
        },
        onZoomChanged: (data) => {
            setZoomLevel(data?.changedValue ?? 1);
        },
        onNavigationComplete: handleNavigationComplete,
    });

    useEffect(() => {
        initMap(mapContainerRef);
        return () => {
            cleanupMapRef.current();
        };
    }, [initMap]);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        const container = mapContainerRef.current;
        if (!container) return;

        cleanupMap();

        let isCancelled = false;
        let debounceTimer: ReturnType<typeof setTimeout> | undefined;

        const tryInitMap = (): void => {
            if (isCancelled || !mapContainerRef.current) return;
            observer.disconnect();
            if (debounceTimer) clearTimeout(debounceTimer);
            clearTimeout(fallbackTimer);
            initMap(mapContainerRef);
        };

        const scheduleInit = (): void => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(tryInitMap, 100);
        };

        const observer = new ResizeObserver(scheduleInit);
        observer.observe(container);
        scheduleInit();
        const fallbackTimer = setTimeout(tryInitMap, 350);

        return () => {
            isCancelled = true;
            observer.disconnect();
            if (debounceTimer) clearTimeout(debounceTimer);
            clearTimeout(fallbackTimer);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenMode]);

    useEffect(() => {
        return () => {
            cleanupMapRef.current();
        };
    }, []);

    useEffect(() => {
        if (contrast) {
            updateObjectGroup(mapData, OBJECT_GROUP);
            updateObjectStyle(currentMap, OBJECT_GROUP);
            setPoi(MAP_CONTRAST_COLOR.POI);
        } else {
            resetObjectStyle(currentMap);
            resetPoi();
        }
    }, [currentMap, mapData, contrast, resetPoi, setPoi]);

    useEffect(() => {
        if (zoomClosed) {
            enableMouseEvent();
        } else {
            disableMouseEvent();
        }
    }, [zoomClosed, enableMouseEvent, disableMouseEvent]);

    useInteraction(containerRef, () => {
        const { isModalInStack } = useModalStackStore.getState();
        const isTimeExtensionModalOpen = isModalInStack('time-extension-modal');
        if (!isTimeExtensionModalOpen) {
            resetCountdown();
        }
    });

    return {
        t,
        mapContainerRef,
        containerRef,
        parkingSlotListRef,
        server,
        screenMode,
        zoomClosed,
        language,
        mapData,
        loading,
        isMapReady,
        selectedFloorName,
        currentBuildingId: currentBuilding?.studioBuildingId,
        isDisabledZoomIn,
        isDisabledZoomOut,
        handleZoomIn,
        handleZoomOut,
        moveUp,
        moveDown,
        moveLeft,
        moveRight,
        showPositionPopup,
        showNavigationGuide,
        navigationStatus,
        selectedPoiRef,
        selectedRoute,
        startPoint,
        navigationGuideEndPoint,
        nearbyPoiList,
        nearbySelectedPoiId,
        nearbyTitle,
        nearbyFloorName,
        floorAriaLabel,
        floorDisplayText,
        menuName,
        parkingSlots,
        selectedSlotId,
        handleSelectSlot,
        handleEndAndReset,
        handleShowMyLocation,
        handleDirections,
        handlePauseSimulation,
        handleResumeSimulation,
        handleWatchAgain,
    };
};
